import React, { useState, useEffect } from 'react';
import { Form, Input, InputNumber, Button, Modal, Select } from 'antd';
import apiService from '../../api/apiService';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function RoomOrderModel({ isOpen, onClose, onSave, existingData, hotelId }) {
  const [form] = Form.useForm();
  const [error, setError] = useState('');
  const [inventoryOptions, setInventoryOptions] = useState([]);
  const [bookingsOptions, setBookingsOptions] = useState([]);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [maxQty, setMaxQty] = useState(0);

  useEffect(() => {
    fetchInventory();
    fetchBookings();
    fetchGuests();
  }, []);

  const fetchInventory = async () => {
    try {
      const response = await apiService.get(`/inventory/?hotel=${hotelId}`);
      setInventoryOptions(
        response.map(item => ({
          value: item.id,
          label: item.item_name,
          quantity: item.quantity,
        }))
      );
    } catch (error) {
      console.error('Error fetching inventory:', error);
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await apiService.get(`/bookings/?hotel=${hotelId}`);
      setBookingsOptions(
        response.map(item => ({
          value: item.id,
          guest: item.guest,
        }))
      );
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGuests = async () => {
    try {
      const response = await apiService.get(`/guests/?hotel=${hotelId}`);
      setGuests(response);
    } catch (error) {
      console.log(error);
    }
  };

  const getGuestName = guestId => {
    const guest = guests.find(g => g.id === guestId);
    return guest?.name || '-';
  };

  useEffect(() => {
    if (existingData) {
      console.log('existingData:', existingData); // Debug check

      form.setFieldsValue({
        order_details: existingData.order_details || '',
        amount: existingData.amount || 0,
        quantity: existingData.quantity || 0,
        booking: existingData.booking || '',
        inventory: existingData.inventory || '',
      });

      if (existingData.inventory && inventoryOptions.length > 0) {
        const qty = getItemQty(existingData.inventory);
        setSelectedItem(existingData.inventory);
        setMaxQty(qty);
      }
    } else {
      form.resetFields();
      setSelectedItem(null);
      setMaxQty(0);
    }
  }, [existingData, inventoryOptions, form]);

  const handleInventoryChange = value => {
    setSelectedItem(value);
    const qty = getItemQty(value);
    setMaxQty(qty);
  };

  const getItemQty = itemId => {
    const item = inventoryOptions.find(i => i.value === itemId);
    return item ? item.quantity : 0;
  };

  const handleSaveAndEdit = async values => {
    const payload = {
      order_details: values.order_details,
      amount: values.amount,
      quantity: values.quantity,
      booking: values.booking,
      inventory: values.inventory,
      hotel: localStorage.getItem('hotel'),
    };

    try {
      let response;
      if (existingData) {
        response = await apiService.put(
          `/room-orders/${existingData.id}/?hotel=${hotelId}`,
          payload
        );
        toast.success('Room order updated successfully!');
      } else {
        response = await apiService.post(
          `/room-orders/?hotel=${hotelId}`,
          payload
        );

        console.log(response);
        toast.success('Room order created successfully!');
      }
      onSave(response);
      handleCancel();
    } catch (error) {
      toast.error('Failed to save room order! Please try again!');
      console.error(error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setError('');
    setSelectedItem(null);
    setMaxQty(0);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal
      centered
      title={existingData ? 'Edit Order' : 'Add Order'}
      open={isOpen}
      onCancel={handleCancel}
      footer={null}
    >
      <Form form={form} layout="vertical" onFinish={handleSaveAndEdit}>
        <Form.Item
          label="Guest Name"
          name="booking"
          rules={[{ required: true, message: 'Please select a booking' }]}
        >
          <Select
            placeholder={loading ? 'Loading...' : 'Select Guest'}
            disabled={loading}
            loading={loading}
            style={{ marginBottom: '8px' }}
          >
            {bookingsOptions.map(option => (
              <Select.Option key={option.value} value={option.value}>
                {getGuestName(option.guest)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Inventory Item"
          name="inventory"
          rules={[
            { required: true, message: 'Please select an inventory item' },
          ]}
        >
          <Select
            placeholder={loading ? 'Loading...' : 'Select inventory item'}
            disabled={loading}
            loading={loading}
            style={{ marginBottom: '8px' }}
            onChange={handleInventoryChange}
          >
            {inventoryOptions.map(option => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Quantity"
          name="quantity"
          rules={[
            { required: true, message: 'Please enter the quantity' },
            () => ({
              validator(_, value) {
                if (!value || value <= maxQty) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error(`Only ${maxQty} available in inventory`)
                );
              },
            }),
          ]}
        >
          <InputNumber min={1} max={maxQty} placeholder={`Max: ${maxQty}`} />
        </Form.Item>

        {selectedItem && (
          <div style={{ marginBottom: 16, color: '#888' }}>
            <strong>Max Available:</strong> {maxQty}
          </div>
        )}

        <Form.Item
          label="Amount"
          name="amount"
          rules={[
            { required: true, message: 'Please enter the amount per item' },
          ]}
        >
          <InputNumber
            min={0}
            placeholder="Enter amount per item"
            controls={false}
          />
        </Form.Item>

        <Form.Item
          label="Order Details"
          name="order_details"
          rules={[{ required: true, message: 'Please enter order details' }]}
          style={{ marginBottom: '8px' }}
        >
          <Input placeholder="Enter order details" />
        </Form.Item>

        <div className="form-actions d-flex justify-content-end">
          <Button onClick={handleCancel} style={{ marginRight: 8 }}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit">
            Save
          </Button>
        </div>
      </Form>
    </Modal>
  );
}

export default RoomOrderModel;
